/**
 * The shell.
 *
 * Four regions: controls on the left, the photograph in the middle, the
 * assistant on the right, the shoot along the bottom. The regions never move,
 * but the two side columns are the photographer's to size, and the assistant
 * folds away entirely when the work is manual.
 */

import { useEffect, useState, type CSSProperties } from 'react';
import { Canvas } from './ui/Canvas.tsx';
import { Chat } from './ui/Chat.tsx';
import { Dropzone } from './ui/Dropzone.tsx';
import { ExportDialog } from './ui/ExportDialog.tsx';
import { Filmstrip } from './ui/Filmstrip.tsx';
import { Opening, useOpening } from './ui/Opening.tsx';
import { Rail } from './ui/Rail.tsx';
import { Resizer } from './ui/Resizer.tsx';
import { Toast } from './ui/Toast.tsx';
import { TopBar } from './ui/TopBar.tsx';
import { useKeyboard } from './hooks/useKeyboard.ts';
import { useStore } from './state/store.ts';

const RAIL = { min: 260, max: 560, preferred: 336 };
const CHAT = { min: 300, max: 640, preferred: 380 };

/**
 * The wrappers take the named grid areas so each resizer has an edge to sit on,
 * and each one restates its area inside itself so `.rail` and `.chat-column`
 * still resolve their own `grid-area` and fill the region top to bottom.
 */
function region(name: 'rail' | 'chat'): CSSProperties {
  return {
    gridArea: name,
    display: 'grid',
    gridTemplateAreas: `"${name}"`,
    gridTemplateColumns: 'minmax(0, 1fr)',
    gridTemplateRows: 'minmax(0, 1fr)',
  };
}

export function App() {
  const hasPhotos = useStore((state) => state.photos.length > 0);
  const railWidth = useStore((state) => state.railWidth);
  const chatWidth = useStore((state) => state.chatWidth);
  const chatOpen = useStore((state) => state.chatOpen);
  const setRailWidth = useStore((state) => state.setRailWidth);
  const setChatWidth = useStore((state) => state.setChatWidth);
  const [exportOpen, setExportOpen] = useState(false);
  const opening = useOpening();

  useKeyboard();

  useEffect(() => {
    const open = () => setExportOpen(true);
    window.addEventListener('lumos:export', open);
    return () => window.removeEventListener('lumos:export', open);
  }, []);

  const layout = !hasPhotos ? ' empty' : chatOpen ? '' : ' no-chat';
  const columns = hasPhotos
    ? { gridTemplateColumns: `${railWidth}px minmax(0, 1fr) ${chatOpen ? chatWidth : 0}px` }
    : undefined;

  return (
    <>
      {opening.showing ? <Opening clearing={opening.clearing} /> : null}

      <div className={`app${layout}${opening.finished ? ' app-enter' : ''}`} style={columns}>
        <TopBar onExport={() => setExportOpen(true)} />

        {hasPhotos ? (
          <div className="rail-wrap" style={region('rail')}>
            <Rail />
            <Resizer
              side="left"
              width={railWidth}
              min={RAIL.min}
              max={RAIL.max}
              defaultWidth={RAIL.preferred}
              onChange={setRailWidth}
              label="Width of the adjustments column"
            />
          </div>
        ) : null}

        <main className="stage">{hasPhotos ? <Canvas /> : <Dropzone />}</main>

        {hasPhotos && chatOpen ? (
          <div className="chat-wrap" style={region('chat')}>
            <Resizer
              side="right"
              width={chatWidth}
              min={CHAT.min}
              max={CHAT.max}
              defaultWidth={CHAT.preferred}
              onChange={setChatWidth}
              label="Width of the assistant column"
            />
            <Chat />
          </div>
        ) : null}

        {hasPhotos ? <Filmstrip /> : null}

        <Toast />
        <ExportDialog open={exportOpen} onClose={() => setExportOpen(false)} />
      </div>
    </>
  );
}
